/* eslint-disable no-shadow */
/* eslint-disable camelcase */
import { Location, LocationDTO, LocationMapper } from '@nx-smartmonkey/shared/domain';
import axios from 'axios';

import { sessionService } from '../application/SessionService';
import { Project } from '../domain/project/Project';
import configuration from './configuration';
import eventsPanel from './events';

const checkAsUser = () => sessionService.getAsUser();

const get = async (call: any, params: any, headers = {}, ignoreAsUser = false) => {
  const url = `${configuration.PLANNER_BACK_END}${call}`;
  return axios.get(url, {
    params: { ...(params ?? {}), ...(checkAsUser() && !ignoreAsUser && { as_user: checkAsUser() }) },
    headers,
  });
};

const post = async (call: any, params: any, headers = {}, ignoreAsUser = false) => {
  const url = `${configuration.PLANNER_BACK_END}${call}`;
  return axios.post(url, params, {
    headers,
    ...(checkAsUser() && !ignoreAsUser && { params: { as_user: checkAsUser() } }),
  });
};

export const getAcquisitionStats = async (startDate: any, endDate = new Date()) => {
  return get(
    `/stats/acquisition`,
    { startDate, endDate },
    { Authorization: `Bearer ${sessionService.getToken()}` },
    true
  );
};

export const getUsageStats = async (startDate: any, endDate = new Date()) => {
  return get(`/stats/usage`, { startDate, endDate }, { Authorization: `Bearer ${sessionService.getToken()}` }, true);
};

export const getAutocompleteGeocode = async ({
  searchValue,
  projectId,
  sessionToken,
}: {
  searchValue: string;
  projectId?: string;
  sessionToken?: string;
}): Promise<Location[]> => {
  const projectIdQuery = `${projectId ? `&project_id=${projectId}` : ``}`;
  const sessionTokenQuery = `${sessionToken ? `&session_token=${sessionToken}` : ``}`;

  const response = await post(
    `/maps/autocomplete?query=${window.encodeURIComponent(searchValue)}${projectIdQuery}${sessionTokenQuery}`,
    {},
    { Authorization: `Bearer ${sessionService.getToken()}` }
  );
  return response.status === 200 ? (response.data as LocationDTO[]).map(LocationMapper.toDomain) : [];
};

export const getLocationFromId = async (locationId: any, sessionToken?: string) => {
  const sessionTokenQuery = `${sessionToken ? `&session_token=${sessionToken}` : ``}`;
  const response = await post(
    `/maps/autocomplete/details?location_id=${locationId}${sessionTokenQuery}`,
    {},
    { Authorization: `Bearer ${sessionService.getToken()}` }
  );
  eventsPanel.geocodeAutocomplete(locationId);

  return response.status === 200 ? response.data : [];
};

export const getLocationFromCoordinates = async (coordinates: { lat: any; lng: any }[]): Promise<Array<Location>> => {
  const response = await post(
    `/maps/reverse-geocode`,
    { coordinates },
    { Authorization: `Bearer ${sessionService.getToken()}` }
  );
  eventsPanel.geocodeReverseGeocode(coordinates);

  if (response.status !== 200) {
    return [];
  }

  const mappedLocations = (response.data as LocationDTO[]).map((loc, index) => {
    return LocationMapper.toDomain(loc ?? coordinates[index]);
  });

  return mappedLocations;
};

export const getUploadAsBase64 = async (url: string): Promise<string | null> => {
  return axios
    .get(url, {
      headers: {
        Authorization: `Bearer ${sessionService.getToken()}`,
      },
      params: { ...(checkAsUser() && { as_user: checkAsUser() }) },
      responseType: `arraybuffer`,
    })
    .then((response) => {
      if (response.status === 200) {
        const image = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ``));
        return `data:${response.headers[`content-type`].toLowerCase()};base64,${image}`;
      }
      return null;
    })
    .catch((_error) => {
      return null;
    });
};

export const dispatchRouteByEmail = async (driverId: any) => {
  return post(`/v2/driver/${driverId}/dispatch`, {}, { Authorization: `Bearer ${sessionService.getToken()}` });
};

export const getPublicVehicleAvatar = async ({
  vehicleId,
  activeProject,
}: {
  vehicleId: string;
  activeProject?: Project;
}): Promise<string | null> => {
  try {
    const response = await axios.get(
      `${configuration.PLANNER_BACK_END}/vehicle/${vehicleId}/avatar?public_key=${encodeURIComponent(
        activeProject?.public_key ?? ``
      )}`,
      {
        responseType: `arraybuffer`,
      }
    );
    if (response.status !== 200) {
      return null;
    }
    return imageArrayBufferToBase64(response.data, response.headers[`content-type`].toLowerCase());
  } catch (error) {
    console.warn(`getPublicVehicleAvatar error:`, error);
    return null;
  }
};

const imageArrayBufferToBase64 = (data: Blob, dataType: string): string => {
  const image = btoa(new Uint8Array(data as any).reduce((data, byte) => data + String.fromCharCode(byte), ``));
  return `data:${dataType};base64,${image}`;
};
